import { isApiError } from "./errors";

export interface PaymentAuthenticationRequired {
  externalId: string;
  status: "requires_action";
  clientSecret: string;
}

/** Parse only the resumable on-session PaymentIntent 402 contract. */
export function getPaymentAuthenticationRequired(
  error: unknown,
): PaymentAuthenticationRequired | null {
  if (
    !isApiError(error) ||
    error.status !== 402 ||
    error.code !== "PAYMENT_AUTHENTICATION_REQUIRED"
  ) {
    return null;
  }
  const externalId =
    error.details?.external_id ?? error.details?.payment_intent_id;
  const status = error.details?.status;
  const clientSecret = error.details?.client_secret;
  if (
    typeof externalId !== "string" ||
    !externalId ||
    status !== "requires_action" ||
    typeof clientSecret !== "string" ||
    !clientSecret
  ) {
    return null;
  }
  return {
    externalId,
    status,
    clientSecret,
  };
}

const TERMINAL_PAYMENT_STATUSES = new Set([
  400, 401, 402, 403, 404, 409, 410, 412, 422,
]);

/** True only when the server conclusively rejected an attempt before replay. */
export function isTerminalPaymentAttemptError(error: unknown): boolean {
  return (
    isApiError(error) &&
    error.code !== "PAYMENT_AUTHENTICATION_REQUIRED" &&
    error.code !== "DROPIN_PAYMENT_UNCERTAIN" &&
    error.code !== "PAYMENT_PENDING" &&
    // A PostgreSQL deadlock is mapped to a retryable 409 after a request may
    // already have a durable payment handoff. Releasing its exact retry key
    // risks a duplicate campaign reservation or charge attempt.
    error.code !== "DEADLOCK_RETRY" &&
    TERMINAL_PAYMENT_STATUSES.has(error.status)
  );
}

/** Preserve, replace, or release a captured authentication attempt after retry. */
export function updatePaymentAuthenticationForError<
  T extends { clientSecret: string },
>(
  current: T | null,
  authenticatedClientSecret: string,
  error: unknown,
): T | null {
  if (!current || current.clientSecret !== authenticatedClientSecret) {
    return current;
  }
  const replacement = getPaymentAuthenticationRequired(error);
  if (replacement) {
    return { ...current, clientSecret: replacement.clientSecret };
  }
  return isTerminalPaymentAttemptError(error) ? null : current;
}
